import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button, Input, MockBadge, Spinner } from "../components/ui";
import { formatApiError } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";

export default function Register() {
  const { register } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await register(email, password, name);
      nav("/");
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid md:grid-cols-2 bg-bg">
      <div className="relative flex items-center justify-center p-6 md:p-10 bg-grid">
        <form
          onSubmit={submit}
          className="w-full max-w-md border border-border bg-surface p-8 reveal"
          data-testid="register-form"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 bg-signal-green grid place-items-center">
              <div className="w-3 h-3 bg-black" />
            </div>
            <div className="font-heading font-black text-lg tracking-tight">immo-watch</div>
            <MockBadge />
          </div>
          <div className="overline">{t("auth.newAnalyst")}</div>
          <h2 className="mt-2 font-heading text-3xl font-bold">{t("auth.createAccount")}</h2>

          <div className="mt-6 space-y-4">
            <div>
              <label className="overline">{t("auth.name")}</label>
              <Input
                data-testid="register-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Patrick J."
                className="mt-1"
              />
            </div>
            <div>
              <label className="overline">{t("auth.email")}</label>
              <Input
                data-testid="register-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1"
              />
            </div>
            <div>
              <label className="overline">{t("auth.password")}</label>
              <Input
                data-testid="register-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="mt-1"
              />
            </div>
            {err && (
              <div
                data-testid="register-error"
                className="border border-signal-red/40 bg-signal-red/10 text-signal-red text-xs px-3 py-2 font-mono"
              >
                {err}
              </div>
            )}
            <Button
              type="submit"
              variant="green"
              className="w-full"
              disabled={busy}
              data-testid="register-submit"
            >
              {busy ? <Spinner /> : "→"} {busy ? t("auth.creating") : t("auth.createAccount")}
            </Button>
          </div>

          <div className="mt-6 pt-6 border-t border-border text-xs">
            <Link
              to="/login"
              data-testid="register-login-link"
              className="font-mono text-xs uppercase tracking-widest2 hover:text-signal-green"
            >
              {t("auth.backToSignIn")}
            </Link>
          </div>
        </form>
      </div>
      <div className="hidden md:block relative overflow-hidden border-l border-border noise">
        <img
          src="https://images.pexels.com/photos/7555429/pexels-photo-7555429.jpeg?auto=compress&cs=tinysrgb&w=1400"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
          alt={t("auth.heroImageAltRegister")}
        />
        <div className="absolute inset-0 bg-gradient-to-bl from-bg via-bg/70 to-transparent" />
        <div className="relative h-full p-10 flex items-end">
          <div>
            <div className="overline">{t("auth.method")}</div>
            <p className="mt-2 max-w-md text-sm text-text-secondary leading-relaxed">
              {t("auth.methodDesc")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
